import { z } from "zod";


const WorkbookSectionsSchema = z.object({
	hotelDescription: z.string().optional().default(""),
	isp: z
		.object({
			metadata: z
				.object({
					provider: z.string().optional().default(""),
					circuitId: z.string().optional().default(""),
					accountNumber: z.string().optional().default(""),
					contact: z.string().optional().default(""),
				})
				.optional()
				.default({}),
			general: z
				.object({
					sla: z.string().optional().default(""),
					handoffType: z.string().optional().default(""),
					escalation: z.string().optional().default(""),
				})
				.optional()
				.default({}),
			network: z
				.object({
					wanCidr: z.string().optional().default(""),
					gateway: z.string().optional().default(""),
					primaryDns: z.string().optional().default(""),
					secondaryDns: z.string().optional().default(""),
				})
				.optional()
				.default({}),
			router: z
				.object({
					model: z.string().optional().default(""),
					lanHandoff: z.string().optional().default(""),
					wanInterface: z.string().optional().default(""),
					notes: z.string().optional().default(""),
				})
				.optional()
				.default({}),
		})
		.optional()
		.default({}),
	equipmentList: z
		.array(
			z.object({
				index: z.string().optional().default(""),
				type: z.string().optional().default(""),
				category: z.string().optional().default(""),
				vendor: z.string().optional().default(""),
				model: z.string().optional().default(""),
				ipAddress: z.string().optional().default(""),
				lanMacAddress: z.string().optional().default(""),
				serialNumber: z.string().optional().default(""),
				name: z.string().optional().default(""),
				location: z.string().optional().default(""),
				deviceMode: z.string().optional().default(""),
				notes: z.string().optional().default(""),
			}),
		)
		.optional()
		.default([]),
	switchPortMap: z
		.array(
			z.object({
				switchName: z.string().optional().default(""),
				port: z.string().optional().default(""),
				linkLocation: z.string().optional().default(""),
				nativeVlan: z.string().optional().default(""),
				allowedVlans: z.string().optional().default(""),
				description: z.string().optional().default(""),
			}),
		)
		.optional()
		.default([]),
	vlanLegend: z
		.array(
			z.object({
				section: z.string().optional().default(""),
				vlanId: z.string().optional().default(""),
				vlanName: z.string().optional().default(""),
				subnet: z.string().optional().default(""),
				defaultGateway: z.string().optional().default(""),
			}),
		)
		.optional()
		.default([]),
	equipmentNotes: z.string().optional().default(""),
	switchPortMapNotes: z.string().optional().default(""),
	vlanLegendNotes: z.string().optional().default(""),
});
export type WorkbookSections = z.infer<typeof WorkbookSectionsSchema>;

const WorkbookSummarySchema = z.object({
	id: z.string(),
	propertyName: z.string(),
	workbookName: z.string(),
	status: z.string(),
	author: z.string(),
	accessLevel: z.string(),
	description: z.string(),
	updatedAt: z.string(),
	versionCount: z.number().int().nonnegative(),
	activeSnapshotId: z.string(),
});
export type WorkbookSummaryApi = z.infer<typeof WorkbookSummarySchema>;

export const WorkbookSummaryListSchema = z.array(WorkbookSummarySchema);

const WorkbookSnapshotSchema = z.object({
	id: z.string(),
	createdAt: z.string(),
	createdBy: z.string(),
	reason: z.string(),
	sourceSnapshotId: z.string().nullable().optional(),
	isActive: z.boolean().optional(),
	data: z.object({
		propertyName: z.string(),
		workbookName: z.string(),
		status: z.string(),
		author: z.string(),
		accessLevel: z.string(),
		description: z.string(),
		sections: WorkbookSectionsSchema.optional(),
	}),
});
export type WorkbookSnapshotApi = z.infer<typeof WorkbookSnapshotSchema>;

export const WorkbookSnapshotListSchema = z.array(WorkbookSnapshotSchema);

const WorkbookEventSchema = z.object({
	id: z.string(),
	eventType: z.string(),
	actor: z.string(),
	occurredAt: z.string(),
	snapshotId: z.string().nullable().optional(),
	payload: z.record(z.string(), z.unknown()).optional().default({}),
});
export type WorkbookEventApi = z.infer<typeof WorkbookEventSchema>;

export const WorkbookEventListSchema = z.array(WorkbookEventSchema);


export const WorkbookCreateResponseSchema = z.object({
	id: z.string(),
	activeSnapshotId: z.string(),
	versionCount: z.number().int().nonnegative(),
});

export const WorkbookSnapshotMutationResponseSchema = z.object({
	workbookId: z.string(),
	snapshotId: z.string(),
	versionCount: z.number().int().nonnegative(),
});

export const WorkbookImportResponseSchema = z.object({
	taskId: z.string(),
	state: z.string(),
	workbookId: z.string(),
});

export const WorkbookExportResponseSchema = z.object({
	taskId: z.string(),
	state: z.string(),
	downloadUrl: z.string(),
	format: z.string(),
});
